import type { Address, Attachment, Email } from 'postal-mime';

import { escapeHtml } from '../../console/cmail/utils/attachmentPreviewUtils';
import { sanitizeEmailHtml } from '../../console/cmail/utils/htmlContentUtils';

const EML_NAME = /\.eml$/i;

/**
 * Whether a document is a saved email. The name is the reliable signal; the
 * signed URL is checked as well for callers that only know the doc id, since
 * its path or its `response-content-disposition` usually carries the file name.
 */
export const isEmlDocument = (fileName?: string, url?: unknown): boolean => {
  if (fileName && EML_NAME.test(fileName.trim())) {
    return true;
  }
  if (typeof url !== 'string') {
    return false;
  }
  try {
    const parsed = new URL(url);
    if (EML_NAME.test(decodeURIComponent(parsed.pathname))) {
      return true;
    }
    const disposition = parsed.searchParams.get('response-content-disposition');
    return Boolean(disposition && /\.eml["']?\s*$/i.test(disposition));
  } catch {
    return false;
  }
};

const formatAddress = (address: Address): string => {
  if (address.group) {
    return `${address.name}: ${address.group.map(formatAddress).join(', ')}`;
  }
  if (address.name && address.address) {
    return `${address.name} <${address.address}>`;
  }
  return address.name || address.address || '';
};

export const formatAddresses = (addresses?: Address | Address[]): string => {
  if (!addresses) {
    return '';
  }
  const list = Array.isArray(addresses) ? addresses : [addresses];
  return list.map(formatAddress).filter(Boolean).join(', ');
};

const formatDate = (date?: string): string => {
  if (!date) {
    return '';
  }
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleString();
};

/**
 * Points `cid:` references at the inline parts they name, so signatures and
 * embedded images render. Expects attachments parsed with base64 encoding.
 */
const inlineContentIds = (html: string, attachments: Attachment[]): string =>
  attachments.reduce((body, attachment) => {
    if (!attachment.contentId || typeof attachment.content !== 'string') {
      return body;
    }
    const cid = attachment.contentId.replace(/^<|>$/g, '');
    const dataUrl = `data:${attachment.mimeType};base64,${attachment.content}`;
    return body.split(`cid:${cid}`).join(dataUrl);
  }, html);

const BODY_SCOPE = '.eml-body';

/**
 * Sanitizes the body through the same pipeline Cmail renders mail with. Its
 * stylesheets are lifted out and scoped to the body, since DOMPurify drops
 * `<style>` tags and the email's rules must not restyle the header above it.
 */
const renderBody = (email: Email): { html: string; css: string } => {
  if (email.html) {
    return sanitizeEmailHtml(
      inlineContentIds(email.html, email.attachments),
      BODY_SCOPE
    );
  }
  return {
    html: `<pre class="eml-text">${escapeHtml(email.text ?? '')}</pre>`,
    css: '',
  };
};

/**
 * Makes CSS safe to place inside a `<style>` element. The sanitizer checks
 * properties, not markup, so a value can still hold `</style >` and close the
 * tag early. `\3C ` is the CSS escape for `<`: it reads the same to CSS and
 * cannot end the element.
 */
const escapeStyleText = (css: string): string => css.replace(/</g, '\\3C ');

const headerRow = (label: string, value: string): string =>
  value ? `<tr><th>${label}</th><td>${escapeHtml(value)}</td></tr>` : '';

/**
 * The page the preview iframe renders: the message headers, its body, and the
 * names of any attachments it carried. Every value comes from the email, which
 * is untrusted, so text is escaped and HTML is sanitized; the iframe is also
 * sandboxed with scripts off.
 */
export const buildEmlDocument = (email: Email): string => {
  const attachmentNames = email.attachments
    .filter((attachment) => attachment.disposition !== 'inline')
    .map((attachment) => attachment.filename || 'Unnamed attachment');

  const attachmentRow = attachmentNames.length
    ? headerRow('Attachments', attachmentNames.join(', '))
    : '';

  const body = renderBody(email);

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <base target="_blank" />
    <style>
      body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1f2937; }
      .eml-header { padding: 1rem 1.25rem; border-bottom: 1px solid #e5e7eb; background: #f9fafb; }
      .eml-subject { margin: 0 0 0.75rem; font-size: 1.25rem; font-weight: 600; }
      .eml-header table { border-collapse: collapse; font-size: 0.875rem; }
      .eml-header th { padding: 0.125rem 1rem 0.125rem 0; text-align: left; vertical-align: top; font-weight: 500; color: #6b7280; white-space: nowrap; }
      .eml-header td { padding: 0.125rem 0; word-break: break-word; }
      .eml-body { padding: 1.25rem; }
      .eml-text { margin: 0; white-space: pre-wrap; font-family: inherit; }
    </style>
    <style>${escapeStyleText(body.css)}</style>
  </head>
  <body>
    <header class="eml-header">
      <h1 class="eml-subject">${escapeHtml(
        email.subject || '(No subject)'
      )}</h1>
      <table>
        ${headerRow('From', formatAddresses(email.from))}
        ${headerRow('To', formatAddresses(email.to))}
        ${headerRow('Cc', formatAddresses(email.cc))}
        ${headerRow('Date', formatDate(email.date))}
        ${attachmentRow}
      </table>
    </header>
    <main class="eml-body">${body.html}</main>
  </body>
</html>`;
};
